// Jurisdiction-aware rules for the EXTRA fields an in-kind contribution needs before
// it can file. These are the one source of truth shared by every surface that lets a
// treasurer designate a contribution In-Kind (the close-ledger "Transaction type"
// dropdown and the bank-transaction classification form) so neither re-derives them.
//
// The field shapes differ by jurisdiction:
//   - description        — required EVERYWHERE (a DB check constraint rejects an
//                          in-kind row with no description), so it is not gated here.
//   - nature (enum)      — required only on Alabama, whose EDI maps `InKindNature`
//                          to `cbInKindNature` and hard-fails filing without it.
//   - valuation basis    — required EVERYWHERE to save (the contribution update path
//                          hard-fails an in-kind row with a blank basis), but its SHAPE
//                          differs: Virginia's COMET Schedule B accepts only a CLOSED
//                          two-value set (Actual Cost | Fair Market Value), so a VA basis
//                          must be one of those members; every other jurisdiction keeps
//                          it as open free text.

import { InKindNature } from '../generated/enums.gen';

/** A treasurer-first option for the in-kind valuation-basis selector. */
export interface InKindValuationBasisOption {
  /** The exact string persisted to `InKindFairMarketValueBasis`. */
  value: string;
  label: string;
}

/**
 * The COMET `valuationBasisType` CLOSED enumeration, authored from
 * `ContributionReport.xsd`. Each stored `value` is the EXACT enumeration member, so once
 * a VA basis is picked here `VirginiaCometValuationBasis.Normalize` maps it back to
 * itself — no silent coercion of the treasurer's intent (unlike a legacy free-text basis,
 * which `Normalize` still collapses onto the nearest member; that is why the ledger
 * re-forces a pick for off-set legacy values). Labels are treasurer-first (sentence
 * case), never the raw enumeration token.
 */
export const IN_KIND_VALUATION_BASIS_OPTIONS: InKindValuationBasisOption[] = [
  { value: 'Actual Cost', label: 'Actual cost' },
  { value: 'Fair Market Value', label: 'Fair market value' },
];

/**
 * Whether the jurisdiction constrains the in-kind valuation basis to the closed COMET
 * set (Virginia only). When true the ledger renders a `<select>` limited to
 * {@link IN_KIND_VALUATION_BASIS_OPTIONS} instead of a free-text input, so the persisted
 * basis maps cleanly to what will file on Schedule B. Ruleset codes are compared
 * case-insensitively (stored codes are `"AL"` / `"federal"` / `"VA"` / `"LA"` / `"NC"`).
 * Never throws.
 */
export function requiresInKindValuationBasis(rulesetCode: string | null | undefined): boolean {
  return (rulesetCode ?? '').toUpperCase() === 'VA';
}

/**
 * Whether a valuation-basis string is one of the closed COMET members
 * ({@link IN_KIND_VALUATION_BASIS_OPTIONS}). Used to gate a Virginia save so a legacy
 * free-text basis (persisted before this control shipped) no longer counts as complete.
 * Exact match — never throws on null/undefined.
 */
export function isClosedInKindValuationBasis(value: string | null | undefined): boolean {
  return IN_KIND_VALUATION_BASIS_OPTIONS.some((option) => option.value === value);
}

/**
 * Whether the jurisdiction requires a structured in-kind NATURE on an in-kind
 * contribution. Alabama-only today. Ruleset codes are compared case-insensitively
 * (stored codes are `"AL"` / `"federal"` / `"VA"` / `"LA"` / `"NC"`), matching the
 * case-insensitive comparison used for the designation picker. Never throws.
 */
export function requiresInKindNature(rulesetCode: string | null | undefined): boolean {
  return (rulesetCode ?? '').toUpperCase() === 'AL';
}

/** The in-kind capture a treasurer supplies when designating a contribution In-Kind. */
export interface InKindCaptureDraft {
  nature: InKindNature | null | undefined;
  description: string | null | undefined;
  fairMarketValueBasis: string | null | undefined;
}

/**
 * Whether an in-kind capture draft is complete enough to SAVE on the given
 * jurisdiction. A description and an FMV basis are required on every jurisdiction (the
 * update path hard-fails without either), and a nature is additionally required where
 * {@link requiresInKindNature} holds (Alabama). On Virginia the basis must further be one
 * of the closed COMET members ({@link isClosedInKindValuationBasis}) — an early nudge that
 * mirrors the Schedule B file-time hard-stop, so a legacy or free-typed basis cannot save.
 * Used to gate the ledger's save so a treasurer never trips the raw server-side
 * requirement. Never throws.
 */
export function isInKindCaptureComplete(
  rulesetCode: string | null | undefined,
  draft: InKindCaptureDraft,
): boolean {
  const trimmedBasis = (draft.fairMarketValueBasis ?? '').trim();
  const hasDescription = (draft.description ?? '').trim() !== '';
  const hasBasis = requiresInKindValuationBasis(rulesetCode)
    ? isClosedInKindValuationBasis(trimmedBasis)
    : trimmedBasis !== '';
  const hasNature = !requiresInKindNature(rulesetCode) || draft.nature != null;
  return hasDescription && hasBasis && hasNature;
}
